import { inject, Injectable } from '@angular/core';
import { QuotesControllerApiService } from '@lx/shared/api';
import { getCompanyHeader } from '@lx/shared/tokens';
import { getLocale } from '@lx/shared/i18n';
import { catchError, map, Observable, of } from 'rxjs';
import { LoggerService } from '@lx/shared/logger';
import { Memoize } from '@lx/shared/utils-http';
import { DeeplinkRequest, DeeplinkResponse } from '@lx/quote-tool/lite/shared/types';
/**
 * Shared deeplink generator service used by both quote-tool-lite and online-quote-tool
 * Calls the backend API to generate deeplinks for sharing quote selections
 */
@Injectable({ providedIn: 'root' })
export class DeeplinkGeneratorService {
  private readonly quotesControllerApiService = inject(QuotesControllerApiService);
  private readonly logger = inject(LoggerService);
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();

  /**
   * Generates a deeplink by calling the backend API
   * @param request The deeplink request to send to the backend
   * @returns Observable emitting the generated deeplink URL or null if generation fails
   */
  generateDeeplink(request: DeeplinkRequest): Observable<string | null> {
    return this.fetchDeeplinkFromApi(request).pipe(
      map((response: DeeplinkResponse) => response.url || null),
      catchError((error) => {
        this.logger.error('Failed to generate deeplink:', error);
        return of(null);
      }),
    );
  }

  /**
   * Fetches deeplink from API with memoization to cache results
   * @param request The deeplink request to submit
   * @returns Observable emitting the deeplink response
   */
  @Memoize((request: DeeplinkRequest) => JSON.stringify(request))
  private fetchDeeplinkFromApi(request: DeeplinkRequest): Observable<DeeplinkResponse> {
    return this.quotesControllerApiService.generateDeeplink(this.locale, this.companyHeader, request) as Observable<DeeplinkResponse>;
  }
}
